// Central map of breadcrumb labels for every routable path.
// Deliberately independent of the router: it only works with path strings,
// so it survives a router change. Router specifics live in routerAdapter.js.

const getModule = ({ topicsStore }, { moduleId }) =>
  topicsStore.getModuleById(moduleId);

const getGroupChat = ({ chatsStore }, id) =>
  chatsStore.getChatById(id, 'groupChats');

export const routes = [
  // student
  { path: '/', label: () => 'Home' },
  {
    path: '/classes/:id',
    label: (ctx, { id }) => getGroupChat(ctx, id)?.chatName,
    fallback: 'Class',
    // full height chat layouts have their own header, a trail would push the message form down
    fullScreen: true,
  },
  {
    path: '/topics/:topicId/modules/:moduleId',
    label: (ctx, params) => getModule(ctx, params)?.moduleName,
    fallback: 'Lesson',
  },
  {
    path: '/topics/:topicId/modules/:moduleId/learning',
    label: () => 'Learn',
  },
  {
    path: '/topics/:topicId/modules/:moduleId/learning/:resourceId',
    label: (ctx, params) =>
      getModule(ctx, params)?.getItemById(
        params.resourceId,
        'learningResources'
      )?.instructions,
    fallback: 'Activity',
  },
  { path: '/topics/:topicId/modules/:moduleId/tasks', label: () => 'Tasks' },
  {
    path: '/topics/:topicId/modules/:moduleId/tasks/:taskId',
    label: (ctx, params) => {
      const task = getModule(ctx, params)?.getItemById(params.taskId, 'tasks');
      return task && `Task ${task.index + 1}`;
    },
    fallback: 'Task',
  },
  { path: '/targets', label: () => 'My Targets' },
  {
    path: '/targets/:targetId',
    label: ({ loggedInUser }, { targetId }) =>
      loggedInUser?.private?.personalTargets.get(targetId)?.targetText,
    fallback: 'Target',
  },
  { path: '/inbox', label: () => 'Inbox' },
  {
    path: '/messages/:chatId',
    label: ({ chatsStore }, { chatId }) =>
      chatsStore.getChatById(chatId, 'individualChats')?.chatName,
    fallback: 'Message',
    fullScreen: true,
  },
  { path: '/profile', label: () => 'My Profile' },
  { path: '/learningPlan/:questionSet', label: () => 'My Learning Plan' },
  { path: '/privacy', label: () => 'Privacy Policy' },

  // staff
  { path: '/staff/classrooms', label: () => 'Classrooms' },
  {
    path: '/staff/classrooms/:groupChatId',
    label: (ctx, { groupChatId }) => getGroupChat(ctx, groupChatId)?.chatName,
    fallback: 'Classroom',
  },
  {
    path: '/staff/users',
    label: ({ loggedInUser }) =>
      loggedInUser?.role === 'admin' ? 'Users' : 'Students',
  },
  {
    path: '/staff/users/:userId',
    label: ({ usersStore }, { userId }) =>
      usersStore.getUserById(userId)?.displayName,
    fallback: 'User',
  },
  { path: '/staff/users/:userId/personal', label: () => 'Personal' },
  { path: '/staff/users/:userId/progress', label: () => 'Progress' },
  { path: '/staff/users/:userId/progress/modules', label: () => 'Modules' },
  {
    path: '/staff/users/:userId/progress/modules/:moduleId',
    label: (ctx, params) => getModule(ctx, params)?.moduleName,
    fallback: 'Module',
  },
  {
    path: '/staff/users/:userId/progress/modules/:moduleId/learning',
    label: () => 'Learning',
  },
  {
    path: '/staff/users/:userId/progress/modules/:moduleId/tasks',
    label: () => 'Tasks',
  },
  {
    path: '/staff/users/:userId/progress/targets',
    label: () => 'Personal Targets',
  },
  { path: '/staff/users/:userId/learningPlans', label: () => 'Learning Plans' },
  {
    path: '/staff/users/:userId/learningPlans/:id/:questionSet',
    label: () => 'Learning Plan',
  },
  { path: '/staff/topics', label: () => 'Topics' },
  {
    path: '/staff/topics/:topicId/modules/:moduleId',
    label: (ctx, params) => getModule(ctx, params)?.moduleName,
    fallback: 'Module',
  },
  {
    path: '/staff/topics/:topicId/modules/:moduleId/:collection',
    label: (ctx, { collection }) =>
      collection === 'tasks' ? 'Tasks' : 'Learning',
  },
  { path: '/staff/search/users', label: () => 'Search Users' },
  { path: '/staff/admin/learningPlans', label: () => 'Learning Plans' },
  {
    path: '/staff/admin/learningPlans/:id/:questionSet',
    label: () => 'Learning Plan',
  },
  { path: '/staff/admin/reports', label: () => 'Reports' },
  { path: '/staff/admin/privacy', label: () => 'Privacy Policies' },
  { path: '/staff/admin/categories', label: () => 'Categories' },
];

const toSegments = (path) => path.split('/').filter(Boolean);

export const matchPath = (pattern, path) => {
  const patternSegments = toSegments(pattern);
  const pathSegments = toSegments(path);
  if (patternSegments.length !== pathSegments.length) return null;

  const params = {};
  for (let i = 0; i < patternSegments.length; i++) {
    const patternSegment = patternSegments[i];
    const pathSegment = decodeURIComponent(pathSegments[i]);
    if (patternSegment.startsWith(':')) {
      params[patternSegment.slice(1)] = pathSegment;
    } else if (patternSegment !== pathSegment) {
      return null;
    }
  }
  return params;
};

// Builds the trail by matching each parent path of the current path.
// Parent paths with no route (e.g. /topics/:topicId) are skipped.
export const getBreadcrumbs = (pathname, ctx, routeList = routes) => {
  const isFullScreen = routeList.some(
    (route) => route.fullScreen && matchPath(route.path, pathname)
  );
  if (isFullScreen) return [];

  const segments = toSegments(pathname);
  const isStaffPath = segments[0] === 'staff';
  const prefixes = segments.map(
    (segment, i) => `/${segments.slice(0, i + 1).join('/')}`
  );
  // students always start from Home, staff start from their section
  if (!isStaffPath) prefixes.unshift('/');

  // a student opened from a classroom leads back to that classroom
  const { origin } = ctx;
  const isStudentPath = isStaffPath && segments[1] === 'users';
  if (isStudentPath && origin?.groupChatId && origin.userId === segments[2]) {
    prefixes.splice(
      prefixes.indexOf('/staff/users'),
      1,
      '/staff/classrooms',
      `/staff/classrooms/${origin.groupChatId}`
    );
  }

  return prefixes.reduce((crumbs, to) => {
    for (const route of routeList) {
      const params = matchPath(route.path, to);
      if (params) {
        const label = route.label(ctx, params) || route.fallback;
        if (label) crumbs.push({ to, label });
        break;
      }
    }
    return crumbs;
  }, []);
};
